import { Callout, MultiSelect, Note } from "@octopusdeploy/design-system-components";
import type { EnvironmentV2Resource, PersistenceSettings, ProcessTemplateParameterResource, ProcessType } from "@octopusdeploy/octopus-server-client";
import { ControlType, Permission } from "@octopusdeploy/octopus-server-client";
import { difference, union, uniq } from "lodash";
import { useMemo } from "react";
import { ProducerTemplatesParameterSelector } from "~/areas/platformHub/processEditor/ProducerTemplatesParameterSelector";
import { isVersionControlledProcess } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { isProcessOrProjectTemplateProcessType } from "~/areas/projects/components/Process/types";
type ArgoCDEnvironmentMultiSelectProps = {
    processType: ProcessType | undefined;
    persistenceSettings: PersistenceSettings | undefined;
    environments: EnvironmentV2Resource[] | undefined;
    selectedEnvironmentIds: string[];
    onSelectedEnvironmentIdsChanged: (newIds: string[]) => void;
    templateParameter: string | null | undefined;
    onTemplateParameterChanged: (newValue: string | null) => void;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void;
    note?: string;
};
export const shouldBlockUserFromRemovingUnavailableEnvironments = (processType: ProcessType | undefined, persistenceSettings: PersistenceSettings | undefined, environments: EnvironmentV2Resource[] | undefined, selectedEnvironmentIds: string[]) => !isVersionControlled(processType, persistenceSettings) && hasUnavailableEnvironments(environments, selectedEnvironmentIds);
const isVersionControlled = (processType: ProcessType | undefined, persistenceSettings: PersistenceSettings | undefined) => persistenceSettings !== undefined && processType !== undefined && isVersionControlledProcess(persistenceSettings, processType);
const getUnavailableEnvironmentIds = (environments: EnvironmentV2Resource[] | undefined, selectedEnvironmentIds: string[]) => {
    const knownEnvironments = uniq((environments || []).map((e) => e.Id));
    return difference(selectedEnvironmentIds, knownEnvironments);
};
const hasUnavailableEnvironments = (environments: EnvironmentV2Resource[] | undefined, selectedEnvironmentIds: string[]) => {
    const unavailableEnvironments = getUnavailableEnvironmentIds(environments, selectedEnvironmentIds);
    return unavailableEnvironments.length > 0;
};
export const ArgoCDEnvironmentMultiSelect = (props: ArgoCDEnvironmentMultiSelectProps) => {
    const { persistenceSettings, processType, environments, onParameterAdded, selectedEnvironmentIds, onSelectedEnvironmentIdsChanged, templateParameter, onTemplateParameterChanged, note } = props;
    const getOption = (env: EnvironmentV2Resource) => ({
        value: env.Id,
        label: env.Name,
        description: env.Description,
    });
    const blockUserFromRemovingUnavailableEnvs = useMemo(() => shouldBlockUserFromRemovingUnavailableEnvironments(processType, persistenceSettings, environments, selectedEnvironmentIds), [environments, persistenceSettings, processType, selectedEnvironmentIds]);
    const unavailableEnvIds = useMemo(() => getUnavailableEnvironmentIds(environments, selectedEnvironmentIds), [environments, selectedEnvironmentIds]);
    const onSelectedChanged = (envIds: string[]) => {
        //Prevent user from removing environments they don't have access to
        if (blockUserFromRemovingUnavailableEnvs) {
            onSelectedEnvironmentIdsChanged(union(envIds, unavailableEnvIds));
        }
        else {
            onSelectedEnvironmentIdsChanged(envIds);
        }
    };
    return (<>
            {isProcessOrProjectTemplateProcessType(processType) ? (<ProducerTemplatesParameterSelector onParameterChange={onTemplateParameterChanged} selectedParameter={templateParameter} controlType={ControlType.Environments} allowClear={true} onParameterAdded={onParameterAdded}/>) : (<MultiSelect value={selectedEnvironmentIds} onChange={onSelectedChanged} getOption={getOption} items={environments || []} label={"Environments"}/>)}
            {note && <Note>{note}</Note>}
            {blockUserFromRemovingUnavailableEnvs && (<Callout type={"information"} title={"Insufficient permissions"}>
                    The {Permission.EnvironmentView} permission is preventing the selected environments from being changed because you do not have access to all environments.
                </Callout>)}
        </>);
};
